// Client library for Outbound HTTP API + Google Sheet Data Fetch (WP-13).
// Provides types and callable wrappers for HTTP API endpoints, execution logs,
// verification testing, import/export, and Google Sheet data fetch campaigns.

import { call } from './callable';

export const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;
export type HttpMethod = (typeof HTTP_METHODS)[number];

export interface ResponseMapping {
  path: string;
  variable: string;
}

export interface HttpApiStats {
  totalCalls: number;
  success: number;
  error: number;
  lastCalledAt?: number | null;
  lastStatus?: number | null;
}

export interface HttpApiDoc {
  id: string;
  tenantId: string;
  name: string;
  method: HttpMethod;
  url: string;
  headers: Record<string, string>;
  secretHeaders: string[];
  bodyTemplate: string;
  responseMapping: ResponseMapping[];
  verified: boolean;
  sampleResponse?: any;
  stats: HttpApiStats;
  createdAt: number;
  updatedAt: number;
}

export interface HttpApiCallDoc {
  id: string;
  tenantId: string;
  apiId: string;
  at: number;
  method: string;
  url: string;
  requestBodySnippet?: string | null;
  responseStatus: number;
  responseBodySnippet?: string | null;
  durationMs: number;
  error?: string | null;
}

export interface SheetFetchDoc {
  id: string;
  tenantId: string;
  name: string;
  accountId: string;
  sheetId: string;
  tab: string;
  keyColumn: string;
  createdAt: number;
  updatedAt: number;
}

export interface VerifyHttpApiResult {
  ok: boolean;
  status: number;
  data: any;
  variables: Record<string, any>;
  durationMs: number;
  error?: string;
}

export interface HttpApiSaveInput {
  id?: string;
  name: string;
  method: HttpMethod;
  url: string;
  headers?: Record<string, string>;
  secretHeaders?: string[];
  bodyTemplate?: string;
  responseMapping?: ResponseMapping[];
}

export interface SheetFetchSaveInput {
  id?: string;
  name: string;
  accountId: string;
  sheetId: string;
  tab?: string;
  keyColumn: string;
}

// ── Callables ────────────────────────────────────────────────────────────────

const _httpApiList = call<void, { ok: boolean; httpApis: HttpApiDoc[] }>('httpApiList');
const _httpApiSave = call<HttpApiSaveInput, { ok: boolean; httpApi: HttpApiDoc }>('httpApiSave');
const _httpApiDelete = call<{ id: string }, { ok: boolean }>('httpApiDelete');
const _httpApiVerify = call<{ id: string; sampleContext?: Record<string, any> }, VerifyHttpApiResult>('httpApiVerify');
const _httpApiCallLog = call<{ id: string; limit?: number }, { ok: boolean; calls: HttpApiCallDoc[] }>('httpApiCallLog');
const _httpApiExport = call<{ id: string }, { ok: boolean; exportConfig: any }>('httpApiExport');
const _httpApiImport = call<{ config: any }, { ok: boolean; id: string }>('httpApiImport');

const _sheetFetchList = call<void, { ok: boolean; fetches: SheetFetchDoc[] }>('sheetFetchList');
const _sheetFetchSave = call<SheetFetchSaveInput, { ok: boolean; fetch: SheetFetchDoc }>('sheetFetchSave');
const _sheetFetchDelete = call<{ id: string }, { ok: boolean }>('sheetFetchDelete');

// ── Outbound HTTP API Functions ──────────────────────────────────────────────

export async function listHttpApis(): Promise<HttpApiDoc[]> {
  const res = await _httpApiList();
  return res.data.httpApis || [];
}

export async function saveHttpApi(input: HttpApiSaveInput): Promise<HttpApiDoc> {
  const res = await _httpApiSave({
    id: input.id,
    name: input.name.trim(),
    method: input.method,
    url: input.url.trim(),
    headers: input.headers || {},
    secretHeaders: input.secretHeaders || [],
    bodyTemplate: input.bodyTemplate || '',
    responseMapping: input.responseMapping || [],
  });
  return res.data.httpApi;
}

export async function deleteHttpApi(id: string): Promise<void> {
  await _httpApiDelete({ id });
}

export async function verifyHttpApi(
  id: string,
  sampleContext: Record<string, any> = {},
): Promise<VerifyHttpApiResult> {
  const res = await _httpApiVerify({ id, sampleContext });
  return res.data;
}

export async function getHttpApiCallLogs(id: string, limit = 50): Promise<HttpApiCallDoc[]> {
  const res = await _httpApiCallLog({ id, limit });
  return res.data.calls || [];
}

export async function exportHttpApi(id: string): Promise<any> {
  const res = await _httpApiExport({ id });
  return res.data.exportConfig;
}

export async function importHttpApi(config: any): Promise<string> {
  const res = await _httpApiImport({ config });
  return res.data.id;
}

// ── Google Sheet Data Fetch Functions ─────────────────────────────────────────

export async function listSheetFetches(): Promise<SheetFetchDoc[]> {
  const res = await _sheetFetchList();
  return res.data.fetches || [];
}

export async function saveSheetFetch(input: SheetFetchSaveInput): Promise<SheetFetchDoc> {
  const res = await _sheetFetchSave({
    id: input.id,
    name: input.name.trim(),
    accountId: input.accountId.trim(),
    sheetId: input.sheetId.trim(),
    tab: (input.tab || 'Sheet1').trim(),
    keyColumn: input.keyColumn.trim(),
  });
  return res.data.fetch;
}

export async function deleteSheetFetch(id: string): Promise<void> {
  await _sheetFetchDelete({ id });
}
